<template>
  <v-dialog
    fullscreen
    persistent
    :model-value="(gettingInitialData || !organization) && !hasNetworkConnectionError"
    transition="fade-transition"
  >
    <v-sheet>
      <v-container style="height: 100vh;" class="d-flex flex-column">
        <v-row justify="center" align="start" class="d-flex">
          <v-col cols="auto">
            <div class="text-disabled">
              {{ t('Loading data') }}
            </div>
          </v-col>
        </v-row>
        <v-row dense class="d-flex flex-row" align="center" justify="center">
          <v-col cols="12" class="text-center flex-column">
            <div class="text-animation">
              <div class="large-outlined">
                Kiosk
              </div>
            </div>
            <div class="powered-by">
          <span>
            <span>
              {{ t('Powered by') }}
            </span>
            <span style="font-weight: 700;">
              Noona
            </span>
          </span>
            </div>
          </v-col>
        </v-row>
        <v-row justify="center">
          <v-col cols="auto">
            <v-progress-linear
              v-show="gettingInitialData"
              indeterminate
              style="width: 100px;"
              height="5"
              color="rgba(34,119,221,1)"
            />
            <v-btn
              v-show="!gettingInitialData && !kioskUUID && !showUUIDOptions"
              variant="outlined"
              class="text-uppercase"
              @click="showUUIDOptions = true"
              @touchend="showUUIDOptions = true"
            >
              {{ t('getStarted') }}
            </v-btn>
	          <v-btn
			          v-if="showUUIDOptions && !gettingInitialData"
			          variant="outlined"
			          class="text-uppercase mr-2"
			          @click="openCamera"
			          @touchend="openCamera"
	          >
		          {{ t('openCamera') }}
	          </v-btn>
	          <v-btn
			          v-if="showUUIDOptions && !gettingInitialData"
			          variant="outlined"
			          class="text-uppercase ml-2"
			          @click="showUUIDTextBox = true"
			          @touchend="showUUIDTextBox = true"
	          >
		          {{ t('Enter code') }}
	          </v-btn>
	          <v-text-field
			          v-if="showUUIDOptions && showUUIDTextBox && !gettingInitialData"
			          variant="outlined"
			          density="compact"
			          :label="t('enterKioskUUID')"
			          class="mr-2"
			          v-model="textBoxUUID"
	          />
	          <v-btn
			          v-if="showUUIDOptions && showUUIDTextBox && !gettingInitialData"
			          class="mt-2"
			          variant="outlined"
			          @click="setUUIDManually"
			          @touchend="setUUIDManually"
	          >
		          {{ t('start') }}
	          </v-btn>
            <div 
              v-show="!gettingInitialData && kioskUUID && !organization" 
              class="text-center"
            >
              <div 
                class="pb-2 text-red text-uppercase" 
                style="font-size: 14px; font-weight: 700;"
              >
                {{ t('No data received') }}
              </div>
              <v-btn
                variant="outlined"
                class="text-uppercase"
                @click="fetchDataAgain"
                @touchend="fetchDataAgain"
              >
                {{ t('Try again') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-sheet>
  </v-dialog>
</template>

<script>
import { useI18n } from "vue-i18n";
import { useStore } from "../stores/store";

// Capacitor imports
import { BarcodeScanner } from '@capacitor-community/barcode-scanner';
import { Preferences } from '@capacitor/preferences';

export default {
  name: "InitialLoadingScreen",
  setup() {
    const store = useStore()
    const { t, te } = useI18n()

    return {
      store,
      t,
      te
    }
  },
  data() {
    return {
      showUUIDOptions: false, 
      showUUIDTextBox: false, 
      textBoxUUID: null,
      clicked: false
    }
  },  
  computed: {
    gettingInitialData() {
      return this.store.gettingInitialData
    },
    organization() {
      if(typeof this.store.kiosk !== 'undefined' && this.store.kiosk !== null) {
        return this.store.kiosk.organization
      }
      return null
    },
    kioskUUID() {
      return this.store.kioskUUID
    },
    hasNetworkConnectionError() {
		return this.store.hasNetworkConnectionError
    }
  },
  methods: {
    async setKioskUUID(uuid) {
      if(typeof uuid !== 'undefined' && uuid !== null) {
        try {
          await Preferences.set({ key: 'sc-kiosk', value: uuid })
        } catch (e) {
          //
        }
      }
      return true
    },
    async checkPermission() {
      try {
        // check if user already granted permission
        const status = await BarcodeScanner.checkPermission({ force: false })

        if (status.granted) {
          // user granted permission
          return true;
        }

        if (status.denied) {
          // user denied permission
          return false;
        }

        // user has not denied permission
        // but the user also has not yet granted the permission
        // so request it
        const statusRequest = await BarcodeScanner.checkPermission({ force: true });

        if (statusRequest.granted) {
          // the user did grant the permission now
          return true;
        }

        // user did not grant the permission, so he must have declined the request
        return false;
      } catch (e) {
        return false
      }
    },

    async setUUIDManually() {
      if(typeof this.textBoxUUID === 'undefined' || this.textBoxUUID === null || this.textBoxUUID === '') {
        return
      }
      this.store.customLogger('info', 'Setting UUID manually')
      this.store.gettingInitialData = true
      await this.setKioskUUID(this.textBoxUUID)
      this.store.kioskUUID = this.textBoxUUID
      await this.store.getKioskData()
    },
    async openCamera() {
      this.store.customLogger('info', 'Opening camera')
      this.showUUIDTextBox = false
      this.store.scanningQRCode = true
      const permission = await this.checkPermission()

      if (permission) {
        try {
          await BarcodeScanner.hideBackground().catch(() => console.log('hide background error'))
          const result = await BarcodeScanner.startScan().catch(() => console.log('start scan error'))

          if (result.hasContent) {
            // Stop the scan here
            await BarcodeScanner.showBackground()
            await BarcodeScanner.stopScan()
            this.store.scanningQRCode = false

            await this.setKioskUUID(result.content)
            await this.store.getKioskData(result.content)
          }
        } catch (e) {
          //
        }
      }
    },
    async fetchDataAgain() {
		if(this.clicked === true) {
			return
		}

		this.clicked = true
		setTimeout(async () => {
			this.store.gettingInitialData = true
			await this.store.getKioskData()
			this.clicked = false
		}, 100)
    }
  }
}
</script>

<style scoped>
.loading:after {
  overflow: hidden;
  display: inline-block;
  vertical-align: bottom;
  -webkit-animation: ellipsis steps(4, end) 900ms infinite;
  animation: ellipsis steps(4, end) 900ms infinite;
  content: "\2026";
  width: 0;
}
@keyframes ellipsis {
  to {
    width: 50px;
  }
}
@-webkit-keyframes ellipsis {
  to {
    width: 50px;
  }
}

.text-animation {
  color: transparent;
  -webkit-text-fill-color: transparent;
  -webkit-background-clip: text;
  background-image: linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(34,119,221,0.5) 10%, rgba(34,119,221,1) 25%, rgba(34,119,221,1) 50%, rgba(34,119,221,1) 75%, rgba(34,119,221,0.5) 90%, rgba(255,255,255,1) 100%);
  background-repeat: repeat;
  animation: blue-animation 45s linear infinite;
  -webkit-animation: blue-animation 45s linear infinite;
}
@keyframes blue-animation {
  to {
    background-position: 1000vh;
  }
}
@-webkit-keyframes blue-animation {
  to {
    background-position: 1000vh;
  }
}

.large-outlined {
  font-size: 144px;
  font-weight: 700;
  text-transform: uppercase;
  color: #FFFFFF;
  line-height: 100%;
}
.medium-outlined {
  font-size: 40px;
  font-weight: 700;
  text-transform: uppercase;
  -webkit-text-stroke: 1px #000000;
  color: #FFFFFF;
}
.small-outlined {
  font-size: 20px;
  font-weight: 700;
  text-transform: uppercase;
  -webkit-text-stroke: 1px #000000;
  color: #FFFFFF;
}
.powered-by {
  font-size: 14px;
  font-weight: 400;
}
</style>